import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import {
  DeleteButton,
  EditButton,
  List,
  ShowButton,
  useDataGrid,
} from "@refinedev/mui";
import React from "react";
import { userDeleteNotifications } from "@/lib/account-deletion";

/**
 * Whole years between `dateOfBirth` and today, or null when there is nothing to
 * work from.
 *
 * The date is compared COMPONENT-WISE off the `YYYY-MM-DD` string rather than
 * through `new Date(value)`. That constructor reads a bare date as UTC midnight,
 * so anywhere west of UTC `getFullYear`/`getMonth`/`getDate` report the day
 * BEFORE the stored birthday — which turns every birthday falling on today into
 * an age one year short. Same reason the column is a `date` server-side and a
 * string everywhere else: this value is a calendar day, not an instant.
 *
 * "Today" is the viewer's local day, which is the right frame for an admin
 * reading their own screen.
 */
const ageFrom = (dateOfBirth?: string | null): number | null => {
  if (!dateOfBirth) {
    return null;
  }

  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth);

  if (!parts) {
    return null;
  }

  const [, year, month, day] = parts.map(Number);
  const today = new Date();
  const hadBirthdayThisYear =
    today.getMonth() + 1 > month ||
    (today.getMonth() + 1 === month && today.getDate() >= day);
  const age = today.getFullYear() - year - (hadBirthdayThisYear ? 0 : 1);

  // The server refuses anything before 1900 or in the future, so a value out of
  // range means the row is wrong rather than the arithmetic. Report it as
  // unreadable and let the cell show the stored date instead of printing a
  // negative age as though it were a fact about a person.
  return age >= 0 && age <= 150 ? age : null;
};

/**
 * `lastPlayAt` is an ISO instant from the server; render it in the viewer's
 * locale, or nothing if it will not parse — the chip still says which client.
 */
const formatInstant = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

/** The three values `story_play_events.client` can hold, in display form. */
const CLIENT_LABEL: Record<string, string> = {
  ios: "iOS",
  android: "Android",
  web: "Web",
};

// While any row on the page has an open deletion, the list re-reads it, so the
// row drops out once the job finishes; otherwise it doesn't poll at all.
const DELETION_REFRESH_MS = 20_000;

// How long typing has to pause before the search goes to the server — one
// request per pause, not one per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

export const UserList = () => {
  const { dataGridProps, filters, setFilters } = useDataGrid({
    queryOptions: {
      refetchInterval: (query) =>
        query.state.data?.data?.some((row) => row.deletion) ? DELETION_REFRESH_MS : false,
    },
  });

  // Forwarded as `?search=`, which the server matches case-insensitively as a
  // substring of email OR name (`UsersRepository.searchFilter`). The input is
  // local state so typing stays instant; the filter follows it after a pause.
  const activeSearch = String(filters.find((f) => "field" in f && f.field === "search")?.value ?? "");
  const [search, setSearch] = React.useState(activeSearch);

  React.useEffect(() => {
    const term = search.trim();

    // Only when the term actually changed: re-applying the same filter would
    // still send the grid back to page 1.
    if (term === activeSearch) {
      return;
    }

    const handle = setTimeout(() => {
      setFilters(term ? [{ field: "search", operator: "eq", value: term }] : [], "replace");
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [search, activeSearch, setFilters]);

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        type: "number",
        minWidth: 50,
        display: "flex",
        align: "left",
        headerAlign: "left",
      },
      {
        field: "email",
        flex: 1,
        headerName: "Email",
        minWidth: 200,
        display: "flex",
      },
      {
        // `users.handle`, the public @handle, stored already normalized. Null
        // for an account that never chose one: the clients then show a handle
        // derived from the name, which is not reserved, so "—" here is the
        // truth rather than a missing derivation. Not sortable for the same
        // reason the age column isn't.
        field: "handle",
        headerName: "Handle",
        minWidth: 140,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.handle ? `@${row.handle}` : <span style={{ opacity: 0.6 }}>—</span>,
      },
      {
        field: "name",
        flex: 1,
        headerName: "Name",
        minWidth: 200,
        display: "flex",
      },
      {
        // Keyed on the stored column, not on an `age` the API does not send:
        // age is derived here, and naming the field after the real one keeps the
        // cell reading the value it actually depends on.
        field: "dateOfBirth",
        headerName: "Age",
        minWidth: 80,
        display: "flex",
        align: "left",
        headerAlign: "left",
        // Not sortable, for the reason the story views list gives: the shared data
        // provider sends page/limit only and drops sorters, so a sort here would
        // silently reorder the current page and nothing else. Sorting by age
        // needs the provider to forward sorters AND the server to order by
        // `date_of_birth` — and note the arrow would then read backwards, since
        // an ascending birthday is a descending age.
        sortable: false,
        renderCell: ({ row }) => {
          const dateOfBirth: string | null = row.dateOfBirth ?? null;
          const age = ageFrom(dateOfBirth);

          // Most rows are this today: `users.date_of_birth` is only asked for by
          // onboarding and the backfill gate, so every account that predates
          // both is still null.
          if (age === null) {
            return dateOfBirth ? (
              <Tooltip title="Not a readable date of birth">
                <span>{dateOfBirth}</span>
              </Tooltip>
            ) : (
              "—"
            );
          }

          // Age is derived; the birthday is what is stored. Showing it on hover
          // means nobody has to open the record to check what the number is
          // actually built from.
          return <Tooltip title={dateOfBirth as string}><span>{age}</span></Tooltip>;
        },
      },
      {
        field: "role",
        headerName: "Role",
        minWidth: 110,
        display: "flex",
        // `users.role` is a free-text column server-side (notNull, default
        // 'default'), not a pg enum — so this renders whatever the row carries
        // rather than mapping a closed set. Only the exact string 'admin' is
        // load-bearing: AdminGuard and RolesGuard both match on it, and it is
        // what lets an account reach this panel at all. Hence the one call-out.
        renderCell: ({ row }) =>
          row.role === "admin" ? (
            <Chip size="small" label="admin" color="warning" />
          ) : (
            <Chip
              size="small"
              label={row.role ?? "default"}
              variant="outlined"
            />
          ),
      },
      {
        field: "credits",
        headerName: "Credits",
        type: "number",
        minWidth: 50,
        display: "flex",
        align: "left",
        headerAlign: "left",
      },
      {
        // `avatarUrl` is not a users column — the server resolves it per page
        // from `users.avatar_asset_id` into `creator_library_assets`
        // (`UserService.findAll`). Not sortable for the same reason the age
        // column isn't: the shared data provider sends page/limit only, and
        // there is no column here to sort by anyway.
        field: "avatarUrl",
        headerName: "Avatar",
        minWidth: 80,
        sortable: false,
        display: "flex",
        // Most accounts have none: a profile picture is opt-in — the player
        // sets it from their profile, or an admin does on the detail page.
        renderCell: ({ row }) =>
          row.avatarUrl ? (
            <Link href={row.avatarUrl} target="_blank" rel="noreferrer">
              <Avatar
                src={row.avatarUrl}
                alt={row.name ?? row.email}
                sx={{ width: 40, height: 40 }}
              />
            </Link>
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        // Not a users column: the server derives it per page from the newest
        // `story_play_events` row that names a client (`UserService.findAll`).
        // It is a PLAY signal, not a last-seen — a creator who edits daily on
        // web and never plays shows "—", and so does anyone whose plays all
        // predate 2026-09-14 (when the client started being recorded) or came
        // from an app build without the `X-App-Platform` header. Not sortable
        // for the same reason the age column isn't.
        field: "lastPlayClient",
        headerName: "Last played on",
        minWidth: 130,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => {
          const client: string | null = row.lastPlayClient ?? null;

          if (!client) {
            return <span style={{ opacity: 0.6 }}>—</span>;
          }

          const when = formatInstant(row.lastPlayAt);
          const chip = (
            <Chip
              size="small"
              variant="outlined"
              label={CLIENT_LABEL[client] ?? client}
            />
          );

          // The chip answers "which"; the hover answers "how stale" — without it
          // a play from March reads the same as one from this morning.
          return when ? <Tooltip title={when}><span>{chip}</span></Tooltip> : chip;
        },
      },
      {
        field: "createdAt",
        flex: 1,
        headerName: "Created At",
        minWidth: 150,
        display: "flex",
      },
      {
        field: "updatedAt",
        flex: 1,
        headerName: "updated At",
        minWidth: 150,
        display: "flex",
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "right",
        headerAlign: "right",
        minWidth: 120,
        sortable: false,
        display: "flex",
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              {/* Schedules the deletion job; the row stays until it finishes
                  (Show User and Account Deletions follow the job). */}
              <DeleteButton hideText recordItemId={row.id} {...userDeleteNotifications} />
            </>
          );
        },
      },
    ],
    []
  );

  return (
    <List>
      <TextField
        size="small"
        label="Search email or name"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, minWidth: 320 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <DataGrid {...(dataGridProps as any)} columns={columns as any} />
    </List>
  );
};
